'use client';
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, ScanLine, HeartPulse, Brain, Wind, Ear, Bone, HeartHandshake, Eye, Microscope, FlaskConical, Search, Phone, MapPin, ArrowLeft, ChevronRight, X, Info, User, AlertCircle, CheckCircle2, Home, FileText } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import TopNav from '@/components/layout/TopNav';
import { diagnosticCategories, searchDiagnostics, getDiagnosticsByCategory } from '@/data/diagnostics';
import type { DiagnosticCategory, DiagnosticService } from '@/data/diagnostics';
import { centres } from '@/data/locations';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { hospitalConfig } from '@/data/hospitalConfig';

import { trackEvent } from '@/lib/analytics';
import {
  generateRequestId,
  buildDiagnosticsWhatsAppMessage,
  openWhatsAppWithMessage,
  type DiagnosticsEnquiryRequest,
} from '@/lib/bookingRequest';

const iconMap: Record<string, React.ElementType> = {
  Droplets, ScanLine, HeartPulse, Brain, Wind, Ear, Bone, HeartHandshake,
  Eye, Microscope, FlaskConical,
};

const colorMap: Record<string, string> = {
  rose: 'text-rose-400 bg-rose-400/10 border-rose-400/20',
  blue: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  red: 'text-red-400 bg-red-400/10 border-red-400/20',
  violet: 'text-violet-400 bg-violet-400/10 border-violet-400/20',
  sky: 'text-sky-400 bg-sky-400/10 border-sky-400/20',
  amber: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  slate: 'text-slate-300 bg-slate-400/10 border-slate-400/20',
  pink: 'text-pink-400 bg-pink-400/10 border-pink-400/20',
  teal: 'text-teal-400 bg-teal-400/10 border-teal-400/20',
  purple: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  green: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
};

type DiagStep = 'categories' | 'services' | 'result' | 'enquiry-details' | 'enquiry-sent';

export default function DiagnosticsContent() {
  const { selectedCentre } = useSelectedCentre();
  const router = useRouter();
  const [step, setStep] = useState<DiagStep>('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DiagnosticCategory | null>(null);
  const [selectedService, setSelectedService] = useState<DiagnosticService | null>(null);
  const [enquiryRequest, setEnquiryRequest] = useState<DiagnosticsEnquiryRequest | null>(null);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchDiagnostics(searchQuery);
  }, [searchQuery]);

  const categoryServices = useMemo(() => {
    if (!selectedCategory) return [];
    const services = getDiagnosticsByCategory(selectedCategory.id);
    if (selectedCentre) {
      return services.filter((s) => s.availableCentreIds.includes(selectedCentre.id));
    }
    return services;
  }, [selectedCategory, selectedCentre]);

  const handleCategorySelect = (cat: DiagnosticCategory) => {
    setSelectedCategory(cat);
    setSearchQuery('');
    setStep('services');
    trackEvent({ event: 'health_checkup_started', source: `diagnostics-category-${cat.id}` });
  };

  const handleServiceSelect = (service: DiagnosticService) => {
    setSelectedService(service);
    setStep('result');
    trackEvent({ event: 'doctor_viewed', source: `diagnostics-service-${service.id}` });
  };

  const handleSearchResultSelect = (service: DiagnosticService) => {
    const cat = diagnosticCategories.find((c) => c.id === service.categoryId);
    setSelectedCategory(cat || null);
    setSelectedService(service);
    setStep('result');
  };

  const handleBack = () => {
    if (step === 'result') {
      setStep('services');
      setSelectedService(null);
    } else if (step === 'services') {
      setStep('categories');
      setSelectedCategory(null);
    } else if (step === 'enquiry-details') {
      setStep('result');
    } else if (step === 'enquiry-sent') {
      setStep('result');
    }
  };

  const getAvailableCentresForService = (service: DiagnosticService) => {
    return centres.filter((c) => service.availableCentreIds.includes(c.id));
  };

  return (
    <main className="journey-bg min-h-screen flex flex-col pb-24" role="main" aria-label="Diagnostics Navigator">
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Back" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div className="w-full max-w-4xl">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-8"
          >
            <p className="section-label mb-2">DIAGNOSTICS NAVIGATOR</p>
            <h1 className="text-display-md text-white font-bold leading-tight mb-2">
              WHAT TEST DO<br />
              <span className="gold-gradient-text">YOU NEED?</span>
            </h1>
            <p className="text-muted-foreground text-sm max-w-lg">
              Find the right diagnostic service at an All Is Well centre.
            </p>
            {selectedCentre && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                <MapPin size={11} className="text-primary" />
                <span className="text-xs text-primary font-medium">{selectedCentre.shortName}</span>
              </div>
            )}
          </motion.div>

          {/* Search bar — always visible on categories/services */}
          {(step === 'categories' || step === 'services') && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mb-6 relative"
            >
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search for a test... e.g. CBC, MRI, Ultrasound"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-10 py-3.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all"
                  aria-label="Search for a diagnostic test"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Search results dropdown */}
              <AnimatePresence>
                {searchQuery && searchResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-20 overflow-hidden"
                  >
                    {searchResults.slice(0, 6).map((service) => {
                      const cat = diagnosticCategories.find((c) => c.id === service.categoryId);
                      return (
                        <button
                          key={service.id}
                          onClick={() => handleSearchResultSelect(service)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 transition-colors text-left border-b border-border/50 last:border-0"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{service.name}</p>
                            <p className="text-xs text-muted-foreground">{cat?.label}</p>
                          </div>
                          <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
                        </button>
                      );
                    })}
                  </motion.div>
                )}
                {searchQuery && searchResults.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl px-4 py-3 z-20"
                  >
                    <p className="text-sm text-muted-foreground">No tests found. Try a different search term.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Back button for sub-steps */}
          {step !== 'categories' && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-6"
              aria-label="Go back"
            >
              <ArrowLeft size={14} />
              <span className="text-xs tracking-widest uppercase">Back</span>
            </motion.button>
          )}

          {/* STEP: Categories */}
          <AnimatePresence mode="wait">
            {step === 'categories' && !searchQuery && (
              <motion.div
                key="categories"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <p className="text-xs text-muted-foreground tracking-widest uppercase mb-4">SELECT A CATEGORY</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {diagnosticCategories.map((cat, idx) => {
                    const IconComp = iconMap[cat.icon] || FlaskConical;
                    const colorClass = colorMap[cat.color] || colorMap.blue;
                    return (
                      <motion.button
                        key={cat.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.04, duration: 0.35 }}
                        whileHover={{ y: -3, scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleCategorySelect(cat)}
                        className="premium-concern-card text-left"
                        aria-label={cat.label}
                      >
                        <div className={`concern-icon-wrap border ${colorClass}`}>
                          <IconComp size={20} aria-hidden="true" />
                        </div>
                        <span className="text-xs font-bold tracking-wide text-foreground leading-snug text-center">
                          {cat.label}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>

                {/* GET MY LAB REPORT — premium action */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 rounded-2xl overflow-hidden border"
                  style={{ borderColor: 'rgba(201,168,76,0.25)', background: 'linear-gradient(135deg, rgba(107,33,212,0.08), rgba(201,168,76,0.05))' }}
                >
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <FileText size={18} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold tracking-widest text-primary/60 uppercase mb-1">PATIENT SERVICE</p>
                        <p className="text-base font-bold text-white mb-1">GET MY LAB REPORT</p>
                        <p className="text-xs text-white/40 leading-relaxed mb-4">
                          Access your latest available lab report securely through WhatsApp.
                        </p>
                        <Link
                          href="/lab-report"
                          className="inline-flex items-center gap-2 btn-outline-gold text-xs px-5 py-2.5"
                          aria-label="Get my lab report on WhatsApp"
                        >
                          GET REPORT ON WHATSAPP
                          <ChevronRight size={12} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <div className="mt-6 p-4 rounded-xl bg-card/50 border border-border/50 flex items-start gap-3">
                  <Info size={14} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Only verified All Is Well diagnostic services are shown. If a service is not listed, please contact your nearest All Is Well centre directly.
                  </p>
                </div>
              </motion.div>
            )}

            {/* STEP: Services in category */}
            {step === 'services' && selectedCategory && (
              <motion.div
                key="services"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <div className="mb-5">
                  <p className="text-xs text-muted-foreground tracking-widest uppercase mb-1">
                    {selectedCategory.label}
                  </p>
                  <h2 className="text-xl font-bold text-white">Available Services</h2>
                  {selectedCentre && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Showing services at {selectedCentre.name}
                    </p>
                  )}
                </div>

                {categoryServices.length === 0 ? (
                  <div className="text-center py-12 px-6 bg-card/50 rounded-2xl border border-border/50">
                    <p className="text-muted-foreground text-sm mb-2">
                      {selectedCentre
                        ? `This service may not be available at ${selectedCentre.shortName} yet.`
                        : 'No services listed in this category yet.'}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      Information coming soon. Please contact the centre directly.
                    </p>
                    <a
                      href={hospitalConfig.phoneTel}
                      className="mt-4 inline-flex items-center gap-2 btn-outline-gold text-xs px-5 py-2.5"
                    >
                      <Phone size={13} />
                      CALL ALL IS WELL
                    </a>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {categoryServices.map((service, idx) => (
                      <motion.button
                        key={service.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => handleServiceSelect(service)}
                        className="premium-option-card p-5 flex items-center gap-4 text-left w-full"
                        aria-label={service.name}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm text-foreground tracking-wide">{service.name}</p>
                          {service.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{service.description}</p>
                          )}
                          {service.preparationNotes && (
                            <p className="text-xs text-primary/70 mt-1">{service.preparationNotes}</p>
                          )}
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP: Result card */}
            {step === 'result' && selectedService && (
              <motion.div
                key="result"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <DiagnosticResultCard
                  service={selectedService}
                  availableCentres={getAvailableCentresForService(selectedService)}
                  selectedCentre={selectedCentre}
                  onEnquireWhatsApp={() => setStep('enquiry-details')}
                />
              </motion.div>
            )}

            {/* STEP: Enquiry patient details */}
            {step === 'enquiry-details' && selectedService && (
              <motion.div
                key="enquiry-details"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <DiagnosticsEnquiryForm
                  service={selectedService}
                  selectedCentre={selectedCentre}
                  selectedCategory={selectedCategory}
                  onSubmit={(req) => {
                    setEnquiryRequest(req);
                    openWhatsAppWithMessage(buildDiagnosticsWhatsAppMessage(req));
                    setStep('enquiry-sent');
                  }}
                />
              </motion.div>
            )}

            {/* STEP: Enquiry sent */}
            {step === 'enquiry-sent' && enquiryRequest && (
              <motion.div
                key="enquiry-sent"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <DiagnosticsEnquirySent
                  request={enquiryRequest}
                  onOpenWhatsAppAgain={() => openWhatsAppWithMessage(buildDiagnosticsWhatsAppMessage(enquiryRequest))}
                  onBackToExperience={() => router.push('/')}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}

// ─── Diagnostic Result Card ───────────────────────────────────────────────────

function DiagnosticResultCard({
  service,
  availableCentres,
  selectedCentre,
  onEnquireWhatsApp,
}: {
  service: DiagnosticService;
  availableCentres: ReturnType<typeof centres>;
  selectedCentre: ReturnType<typeof useSelectedCentre>['selectedCentre'];
  onEnquireWhatsApp: () => void;
}) {
  const cat = diagnosticCategories.find((c) => c.id === service.categoryId);
  const displayCentres = selectedCentre
    ? availableCentres.filter((c) => c.id === selectedCentre.id)
    : availableCentres;

  return (
    <div className="space-y-4">
      {/* Main card */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <p className="text-xs text-muted-foreground tracking-widest uppercase mb-1">{cat?.label}</p>
        <h2 className="text-2xl font-bold text-white mb-1">{service.name}</h2>
        {service.description && (
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{service.description}</p>
        )}

        {service.preparationNotes && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/15 mb-4">
            <Info size={13} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-primary/80">{service.preparationNotes}</p>
          </div>
        )}

        <div className="mb-4">
          <p className="text-xs text-muted-foreground tracking-widest uppercase mb-2">AVAILABLE AT</p>
          {displayCentres.length > 0 ? (
            <div className="flex flex-col gap-2">
              {displayCentres.map((centre) => (
                <div key={centre.id} className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 border border-border/50">
                  <MapPin size={13} className="text-primary flex-shrink-0" />
                  <span className="text-sm text-foreground">{centre.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Information coming soon</p>
          )}
        </div>
      </div>

      {/* WhatsApp Enquiry CTA */}
      <button
        onClick={onEnquireWhatsApp}
        className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300"
        style={{ background: '#25D366', color: '#fff' }}
        aria-label={`Enquire about ${service.name} on WhatsApp`}
      >
        <WhatsAppIconSmall />
        ENQUIRE ON WHATSAPP
        <ChevronRight size={16} />
      </button>

      {/* Secondary actions */}
      <div className="grid grid-cols-2 gap-3">
        <a
          href={hospitalConfig.phoneTel}
          className="flex items-center justify-center gap-2 btn-outline-gold text-xs px-4 py-3"
          aria-label="Call All Is Well"
        >
          <Phone size={14} />
          CALL
        </a>
        <a
          href={hospitalConfig.maps}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 btn-ghost-white text-xs px-4 py-3"
          aria-label="Get directions to All Is Well"
        >
          <MapPin size={14} />
          DIRECTIONS
        </a>
      </div>

      <p className="text-xs text-muted-foreground/60 text-center leading-relaxed px-4">
        Online diagnostic booking is not yet available. Please contact the centre directly to schedule your test.
      </p>
    </div>
  );
}

// ─── Diagnostics Enquiry Form ─────────────────────────────────────────────────

function DiagnosticsEnquiryForm({
  service,
  selectedCentre,
  selectedCategory,
  onSubmit,
}: {
  service: DiagnosticService;
  selectedCentre: ReturnType<typeof useSelectedCentre>['selectedCentre'];
  selectedCategory: DiagnosticCategory | null;
  onSubmit: (req: DiagnosticsEnquiryRequest) => void;
}) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [errors, setErrors] = useState<{ name?: string; mobile?: string }>({});

  const validate = (): boolean => {
    const errs: { name?: string; mobile?: string } = {};
    if (!name.trim()) errs.name = 'Full name is required';
    if (!mobile.trim()) {
      errs.mobile = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(mobile.replace(/\s/g, ''))) {
      errs.mobile = 'Enter a valid 10-digit mobile number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const req: DiagnosticsEnquiryRequest = {
      requestId: generateRequestId(),
      patientName: name.trim(),
      patientMobile: mobile.trim(),
      centreName: selectedCentre?.name ?? 'All Is Well Centre',
      testName: service.name,
      category: selectedCategory?.label ?? service.categoryId,
      createdAt: new Date().toISOString(),
    };
    onSubmit(req);
  };

  return (
    <div>
      <div className="mb-6">
        <p className="text-xs text-muted-foreground tracking-widest uppercase mb-1">DIAGNOSTICS ENQUIRY</p>
        <h2 className="text-xl font-bold text-white mb-1">{service.name}</h2>
        <p className="text-sm text-muted-foreground">Enter your details to send an enquiry on WhatsApp.</p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-xs text-white/50 tracking-widest uppercase mb-2" htmlFor="diag-name">
            FULL NAME <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <User size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              id="diag-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              className={`w-full pl-11 pr-4 py-3.5 bg-white/5 border rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:ring-1 transition-all ${
                errors.name ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/20' : 'border-white/10 focus:border-primary/50 focus:ring-primary/20'
              }`}
            />
          </div>
          {errors.name && (
            <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
              <AlertCircle size={11} />{errors.name}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs text-white/50 tracking-widest uppercase mb-2" htmlFor="diag-mobile">
            MOBILE NUMBER <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <Phone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              id="diag-mobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="10-digit mobile number"
              maxLength={10}
              className={`w-full pl-11 pr-4 py-3.5 bg-white/5 border rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:ring-1 transition-all ${
                errors.mobile ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/20' : 'border-white/10 focus:border-primary/50 focus:ring-primary/20'
              }`}
            />
          </div>
          {errors.mobile && (
            <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
              <AlertCircle size={11} />{errors.mobile}
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-white/25 mb-6 leading-relaxed">
        Only your name and mobile number are needed. No medical history or diagnosis is required.
      </p>

      <button
        onClick={handleSubmit}
        className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300"
        style={{ background: '#25D366', color: '#fff' }}
        aria-label="Send diagnostics enquiry on WhatsApp"
      >
        <WhatsAppIconSmall />
        SEND ENQUIRY ON WHATSAPP
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ─── Diagnostics Enquiry Sent ─────────────────────────────────────────────────

function DiagnosticsEnquirySent({
  request,
  onOpenWhatsAppAgain,
  onBackToExperience,
}: {
  request: DiagnosticsEnquiryRequest;
  onOpenWhatsAppAgain: () => void;
  onBackToExperience: () => void;
}) {
  return (
    <div>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        className="flex flex-col items-center text-center mb-8 pt-4"
      >
        <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
          style={{ background: 'rgba(37,211,102,0.12)', border: '1.5px solid rgba(37,211,102,0.3)' }}>
          <CheckCircle2 size={36} style={{ color: '#25D366' }} />
        </div>
        <h2 className="text-2xl font-bold text-white tracking-tight mb-3">REQUEST SENT</h2>
        <p className="text-sm text-white/50 leading-relaxed max-w-sm">
          Your diagnostics enquiry has been prepared. Please send the WhatsApp message to complete the request.
        </p>
      </motion.div>

      <div className="mb-5 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
        <p className="text-xs font-bold text-amber-400 tracking-wider mb-1.5">IMPORTANT</p>
        <p className="text-xs text-white/50 leading-relaxed">
          The All Is Well team will check availability and provide information. This is an enquiry — not a confirmed booking.
        </p>
      </div>

      <div className="mb-6 p-4 rounded-xl border border-primary/20 bg-primary/5">
        <p className="text-xs text-white/40 tracking-widest uppercase mb-1">ENQUIRY ID</p>
        <p className="text-base font-bold text-primary tracking-wider font-mono">{request.requestId}</p>
        <p className="text-xs text-white/30 mt-1">Keep this ID for your reference.</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onOpenWhatsAppAgain}
          className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-sm tracking-widest uppercase transition-all duration-300"
          style={{ background: 'rgba(37,211,102,0.12)', color: '#25D366', border: '1.5px solid rgba(37,211,102,0.3)' }}
          aria-label="Open WhatsApp again"
        >
          <WhatsAppIconSmall />
          OPEN WHATSAPP AGAIN
          <ChevronRight size={16} />
        </button>
        <button
          onClick={onBackToExperience}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-white/10 bg-white/3 text-white/60 hover:text-white/90 hover:border-white/20 font-semibold text-xs tracking-widest uppercase transition-all duration-200"
          aria-label="Return to All Is Well Experience"
        >
          <Home size={14} />
          BACK TO ALL IS WELL EXPERIENCE
        </button>
      </div>
    </div>
  );
}

// ─── WhatsApp Icon ────────────────────────────────────────────────────────────

function WhatsAppIconSmall() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
